import Avatar from '@/components/common/Avatar';
import useInertiaData from '@/hooks/useInertiaData';
import useRoutes from '@/hooks/useRoutes';
import { User } from '@/types';
import { Popover } from '@headlessui/react';
import { Link } from '@inertiajs/react';
import {
    Award,
    ChevronDown,
    LogOut,
    Shield,
    User as UserIcon,
    UserX,
} from 'lucide-react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const UserDropdown: React.FC = () => {
    const { user, is_impersonating } = useInertiaData();
    const { route } = useRoutes();
    const isImpersonating = Boolean(is_impersonating);

    const { t } = useTranslation();

    const userMenuItems = useMemo(() => {
        const baseMenu: {
            id: string;
            label: string;
            icon: React.ElementType;
            href: string;
            method?: string;
            divider?: boolean;
            element?: 'anchor' | 'link';
            target?: '_blank' | '_self' | '_parent' | '_top';
        }[] = [];

        // Add "Leave impersonation" button at the top if impersonating
        if (isImpersonating) {
            baseMenu.push({
                id: 'leave-impersonation',
                label: 'Leave Impersonation',
                icon: UserX,
                href: '/impersonate/stop/',
                element: 'anchor',
                divider: true,
            });
        }

        // Add staff admin link
        if (user?.is_staff) {
            baseMenu.push({
                id: 'admin',
                label: t('common.admin'),
                icon: Shield,
                href: route('index').toString(),
                element: 'anchor',
                target: '_blank',
            });
        }

        // Add regular menu items
        baseMenu.push(
            {
                id: 'mi-cuenta',
                label: t('userAccount.myAccount'),
                icon: UserIcon,
                href: route('my-account').toString(),
            },
            {
                id: 'cerrar-sesion',
                label: t('userAccount.logout'),
                icon: LogOut,
                divider: true,
                href: route('logout').toString(),
                method: 'post',
            }
        );
        return baseMenu;
    }, [isImpersonating, user?.is_staff, t, route]);

    return (
        <Popover className="relative">
            {({ open }) => (
                <>
                    <Popover.Button className="flex items-center space-x-3 rounded-lg border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 px-3 py-2 transition-colors duration-200 hover:from-amber-100 hover:to-orange-100 focus:ring-2 focus:ring-amber-500 focus:outline-none sm:px-4">
                        <Avatar
                            user={user as User}
                            size="sm"
                            className="sm:h-10 sm:w-10"
                        />
                        <div className="hidden sm:block">
                            <p className="max-w-32 truncate text-sm font-semibold text-gray-900 lg:max-w-none">
                                {user?.first_name}
                            </p>
                        </div>
                        <ChevronDown
                            size={16}
                            className={`text-gray-500 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
                        />
                    </Popover.Button>

                    <Popover.Panel className="absolute top-full right-0 z-20 mt-2 w-64 rounded-lg border border-gray-200 bg-white shadow-xl">
                        {/* User Info Header */}
                        <div className="border-b border-gray-100 p-4">
                            <div className="flex items-center space-x-3">
                                <Avatar user={user as User} size="md" />
                                <div>
                                    <p className="text-sm font-semibold text-gray-900">
                                        {user?.first_name}
                                    </p>
                                </div>
                            </div>
                            <div className="mt-1 flex items-center space-x-1">
                                <Award size={14} className="text-amber-500" />
                                <span className="text-xs font-medium text-amber-700">
                                    {t('home.conquerRanks')}
                                </span>
                                <span className="text-xs font-bold text-orange-600">
                                    {user?.rank?.name || t('home.noRank')}
                                </span>
                            </div>
                        </div>

                        {/* Menu Items */}
                        <div className="py-2">
                            {userMenuItems.map((item) => {
                                const Icon = item.icon;
                                return (
                                    <div key={item.id}>
                                        {item.divider && (
                                            <div className="my-2 border-t border-gray-100" />
                                        )}
                                        {item.element === 'anchor' ? (
                                            <a
                                                href={item.href}
                                                className="flex w-full items-center space-x-3 px-4 py-2 text-left text-sm text-gray-700 transition-colors duration-200 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
                                                target={item.target}
                                            >
                                                <Icon
                                                    size={16}
                                                    className="text-gray-500"
                                                />
                                                <span>{item.label}</span>
                                            </a>
                                        ) : (
                                            <Link
                                                method={
                                                    (item.method ??
                                                        'get') as any
                                                }
                                                href={item.href}
                                                className="flex w-full items-center space-x-3 px-4 py-2 text-left text-sm text-gray-700 transition-colors duration-200 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
                                            >
                                                <Icon
                                                    size={16}
                                                    className="text-gray-500"
                                                />
                                                <span>{item.label}</span>
                                            </Link>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </Popover.Panel>
                </>
            )}
        </Popover>
    );
};

export default UserDropdown;
